import { useEffect, useState } from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { Send, MailOpen, Reply, AlertTriangle, Activity, Users } from "lucide-react";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";

const KPI_DEFS = [
  { key: "sent", label: "Sent", icon: Send },
  { key: "open_rate", label: "Open rate", icon: MailOpen, suffix: "%" },
  { key: "reply_rate", label: "Reply rate", icon: Reply, suffix: "%" },
  { key: "bounce_rate", label: "Bounce rate", icon: AlertTriangle, suffix: "%" },
  { key: "active_campaigns", label: "Active campaigns", icon: Activity },
  { key: "total_leads", label: "Total leads", icon: Users },
];

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/analytics/global").then((r) => setStats(r.data)).catch(() => {});
    api.get("/campaigns").then((r) => setCampaigns(r.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-neutral-500 font-mono">
            Workspace overview
          </div>
          <h1 className="font-display text-4xl font-semibold tracking-tight mt-2">
            Dashboard
          </h1>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {KPI_DEFS.map(({ key, label, icon: Icon, suffix }) => (
          <div
            key={key}
            data-testid={`kpi-${key}`}
            className="tactile border border-[#1a1a1a] bg-[#0d0d0d] rounded-lg p-4 hover:bg-[#111] hover:-translate-y-[1px]"
          >
            <div className="flex items-center justify-between text-neutral-500">
              <span className="text-[10px] uppercase tracking-[0.18em] font-mono">
                {label}
              </span>
              <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
            </div>
            <div className="mt-3 font-mono text-2xl">
              {stats ? (
                <span className="data-swap-ready" key={`v-${key}-${stats[key]}`}>
                  {stats[key]}
                  {suffix && <span className="text-sm text-neutral-500">{suffix}</span>}
                </span>
              ) : (
                <span className="text-neutral-700">-</span>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="border border-[#1a1a1a] bg-[#0d0d0d] rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 font-mono">
              Last 14 days
            </div>
            <h3 className="font-display text-lg mt-1">Sending activity</h3>
          </div>
        </div>
        <div className="h-72 mt-4 min-h-[280px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={stats?.timeseries || []}>
              <defs>
                <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#fafafa" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#fafafa" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a" vertical={false} />
              <XAxis
                dataKey="date"
                tickFormatter={(v) => v.slice(5)}
                stroke="#525252"
                fontSize={11}
              />
              <YAxis stroke="#525252" fontSize={11} allowDecimals={false} />
              <Tooltip
                contentStyle={{
                  background: "#0a0a0a",
                  border: "1px solid #1f1f1f",
                  fontFamily: "JetBrains Mono",
                  fontSize: 12,
                }}
                labelStyle={{ color: "#a1a1aa" }}
              />
              <Area
                type="monotone"
                dataKey="sent"
                stroke="#fafafa"
                fill="url(#g1)"
                strokeWidth={1.5}
              />
              <Area
                type="monotone"
                dataKey="opens"
                stroke="#10b981"
                fill="transparent"
                strokeWidth={1.5}
              />
              <Area
                type="monotone"
                dataKey="clicks"
                stroke="#0ea5e9"
                fill="transparent"
                strokeWidth={1.5}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="border border-[#1a1a1a] bg-[#0d0d0d] rounded-lg overflow-hidden">
        <div className="px-6 py-4 flex items-center justify-between border-b border-[#1a1a1a]">
          <h3 className="font-display text-lg">Recent campaigns</h3>
          <button
            onClick={() => navigate("/campaigns")}
            data-testid="view-all-campaigns"
            className="text-xs text-neutral-400 hover:text-white font-mono uppercase tracking-[0.2em]"
          >
            View all
          </button>
        </div>
        {campaigns.length === 0 ? (
          <div className="p-10 text-center text-neutral-500 text-sm">
            No campaigns yet -{" "}
            <button
              className="underline hover:text-white"
              onClick={() => navigate("/campaigns/new")}
              data-testid="empty-create-campaign"
            >
              create your first
            </button>
            .
          </div>
        ) : (
          <table className="w-full">
            <thead className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-mono">
              <tr className="border-b border-[#1a1a1a]">
                <th className="text-left px-6 py-3">Name</th>
                <th className="text-left px-6 py-3">Status</th>
                <th className="text-right px-6 py-3">Leads</th>
                <th className="text-right px-6 py-3">Sent</th>
                <th className="text-right px-6 py-3">Open %</th>
                <th className="text-right px-6 py-3">Reply %</th>
              </tr>
            </thead>
            <tbody className="font-mono text-sm">
              {campaigns.slice(0, 6).map((c) => (
                <tr
                  key={c.id}
                  className="border-b border-[#1a1a1a] hover:bg-[#111] cursor-pointer"
                  onClick={() => navigate(`/campaigns/${c.id}`)}
                  data-testid={`campaign-row-${c.id}`}
                >
                  <td className="px-6 py-3 text-white">{c.name}</td>
                  <td className="px-6 py-3 text-neutral-400 capitalize">{c.status}</td>
                  <td className="px-6 py-3 text-right">{c.lead_count}</td>
                  <td className="px-6 py-3 text-right">{c.sent_count}</td>
                  <td className="px-6 py-3 text-right">{c.open_rate}%</td>
                  <td className="px-6 py-3 text-right">{c.reply_rate}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
